import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { push } from 'redux-first-router';
import { RouterLink } from '@redux-first-router-link';
import { useTracking } from 'react-tracking';

import { Button } from '@simplywallst/ui-core';
import {
  Modal,
  Header,
  Body,
  Footer,
} from '@simplywallst/ui-core/components/Modal';
import { getDashboardEvent } from '@simplywallst/services';
import { VirtualRenderer } from '@components/VirtualRenderer/VirtualRenderer';
import { getBrief } from '@components/Dashboard/hooks/useFeed/selector';
import { BriefEducationalContent } from '../Dashboard/components/EducationalContent/BriefEducationalContent';

import {
  BriefModalContent,
  BriefModalTitle,
  StyledColumn,
  StyledGrid,
} from './styled';
import { useCompanyNoteModal } from '@components/Note';
import { withAuth } from '@/utilities/queries';
import useUser from '@/hooks/useUser';

export interface Props {
  onClose: () => void;
  companyId: string;
  activityId: string;
  shouldShowLink?: boolean;
}

const BriefModal = ({
  onClose,
  companyId,
  activityId,
  shouldShowLink,
}: Props) => {
  const { openNoteModal, modalState } = useCompanyNoteModal();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'brief',
  });
  const { data: userData } = useUser();
  const { loggedIn } = userData;
  const { data, isFetching, isFetched } = useQuery({
    queryKey: ['brief', activityId, companyId, loggedIn],
    queryFn: async () => {
      try {
        let response: undefined | Awaited<ReturnType<typeof getDashboardEvent>>;

        if (loggedIn) {
          response = await withAuth(getDashboardEvent, {
            activityId,
            companyId,
          });
        } else {
          response = await getDashboardEvent({
            activityId,
            companyId,
          });
        }

        const {
          data: { events, canonical_url, ticker_symbol, name },
        } = response.data;
        if (events.data.length > 0 && events.data[0].type === 'brief') {
          const brief = getBrief(events.data[0], canonical_url);
          return {
            name,
            tickerSymbol: ticker_symbol,
            ...brief,
          };
        }
      } catch (e) {
        console.error(e);
        onClose();
      }
    },
    refetchOnMount: false,
    staleTime: 3600000,
  });

  const handleNavigation = useCallback(() => {
    if (typeof data?.url === 'string') {
      push(data.url);
    }
  }, [data]);

  const addNote = useCallback(() => {
    openNoteModal({ companyId, uniqueSymbol: data?.tickerSymbol || '' });
    trackEvent({
      action: 'click',
      modifier: 'addNote',
    });
    onClose();
  }, [openNoteModal, modalState, trackEvent, data?.tickerSymbol]);

  return (
    <Track>
      <Modal
        isNaked={true}
        isLoading={isFetched === false && isFetching}
        title={
          data && (
            <BriefModalTitle>
              <RouterLink noUnderline to={data.url}>
                {data.tickerSymbol}
              </RouterLink>
              {' | '}
              {data.name}
            </BriefModalTitle>
          )
        }
        fullscreen={true}
        onClose={onClose}
      >
        <StyledColumn
          as="section"
          maxWidth="700px"
          alignSelfLg="center"
          height="100%"
        >
          <Header
            title={
              data && (
                <BriefModalTitle>
                  <RouterLink noUnderline to={data.url}>
                    {data.tickerSymbol}
                  </RouterLink>
                  {' | '}
                  {data.name}
                </BriefModalTitle>
              )
            }
            onClose={onClose}
          />
          <Body scrollable={true} data-cy-id="modal-body">
            <BriefModalContent data-cy-id="brief-modal-content">
              {data && (
                <>
                  <span>{data.dateFormatted}</span>
                  <h2>{data.title}</h2>
                  {typeof data.virtual !== 'undefined' &&
                  data.virtual.length > 0 ? (
                    <VirtualRenderer
                      nodes={data.virtual}
                      options={{
                        allowedNodeTypes: ['p', 'ul', 'li'],
                        allowedAttributes: [],
                      }}
                    />
                  ) : (
                    <p>{data.text}</p>
                  )}
                  <BriefEducationalContent briefType={data?.briefName} />
                </>
              )}
            </BriefModalContent>
          </Body>
          <Footer justifyContent="flex-end">
            <StyledGrid
              templateColumns="max-content max-content max-content"
              gap="4px"
              justifyContent="end"
            >
              <Button
                type="button"
                styleType="gentleLargeReverse"
                marginRight
                displaySmDown="block"
                onClick={addNote}
                data-cy-id="add-note-button"
              >
                Add note
              </Button>
              {shouldShowLink && (
                <Button
                  type="button"
                  styleType="gentleLargeReverse"
                  marginRight
                  displaySmDown="block"
                  onClick={handleNavigation}
                  data-cy-id="modal-close-button"
                >
                  See Company Report
                </Button>
              )}
              <Button
                styleType="louderLarge"
                displaySmDown="block"
                onClick={onClose}
                data-cy-id="modal-confirm-button"
              >
                Close
              </Button>
            </StyledGrid>
          </Footer>
        </StyledColumn>
      </Modal>
    </Track>
  );
};

export default BriefModal;
